import { useFocusRing } from '@react-native-aria/focus'
import { useHover } from '@react-native-aria/interactions'
import { mergeProps } from '@react-native-aria/utils'
import clsx from 'clsx'
import { Image } from 'expo-image'
import { useCallback, useMemo, useState } from 'react'
import type { LayoutChangeEvent, View } from 'react-native'

import type { SlotsToClasses } from '@/src/types/slot-to-classes'

import { avatar, type TAvatarSlots, type TAvatarVariantProps } from './avatar-variants'

interface IProps {
  /**
   * Image source.
   */
  src?: string | null
  /**
   * Image alt text.
   */
  alt?: string
  /**
   * Whether the avatar can be focused.
   * @default false
   */
  isFocusable?: boolean
  /**
   * If `false`, the avatar will show the background color while loading.
   */
  showFallback?: boolean

  /**
   * The component used to render the image.
   * @default "img"
   */
  ImgComponent?: React.ElementType
  /**
   * Props to pass to the image component.
   */
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>

  className?: string

  classNames?: SlotsToClasses<TAvatarSlots>

  testID?: string

  onLayout?: (event: LayoutChangeEvent) => void

  /**
   * Ref to the DOM node.
   */
  ref?: React.Ref<View>
}

export type TUseAvatarProps = IProps & Omit<TAvatarVariantProps, 'children' | 'isInGroup' | 'isInGridGroup'>

export function useAvatar(props: TUseAvatarProps = {}) {
  const {
    src,
    classNames,
    alt = 'avatar',
    isFocusable = false,
    showFallback: showFallbackProp = true,
    ImgComponent = Image,
    imgProps,
    className,
    size: _,
    ...otherProps
  } = props

  const size = props.size ?? 'md'

  /**
   * Fallback avatar applies under 2 conditions:
   * - If `src` was passed and the image has not loaded or failed to load
   * - If `src` wasn't passed
   *
   * In this case, we'll show either the name avatar or default avatar
   */
  const [showFallback, setShowFallback] = useState(!src && showFallbackProp)

  const { isFocusVisible, focusProps } = useFocusRing()
  const { isHovered, hoverProps } = useHover({})

  const slots = useMemo(() => avatar({ size }), [size])

  const baseStyles = clsx(classNames?.base, className)

  const canBeFocused = useMemo(() => {
    return isFocusable
  }, [isFocusable])

  const onError = useCallback(() => {
    setShowFallback(true)
  }, [])

  const getAvatarProps = useCallback(
    () =>
      ({
        tabIndex: canBeFocused ? 0 : -1,
        dataSet: {
          hover: isHovered,
          'focus-visible': isFocusVisible,
        },
        className: slots.base({
          class: clsx(baseStyles, className),
        }),
        ...mergeProps(otherProps, hoverProps, canBeFocused ? focusProps : {}),
      }) as const,
    [baseStyles, canBeFocused, className, focusProps, hoverProps, isFocusVisible, isHovered, otherProps, slots],
  )

  const getImageProps = useCallback(
    (imageProps = {}) => ({
      source:
        typeof src === 'string'
          ? {
              uri: src,
            }
          : src,
      'data-loaded': !!src,
      className: slots.img({ class: classNames?.img, size }),
      onError,
      ...mergeProps(imgProps, imageProps),
    }),
    [classNames?.img, imgProps, onError, size, slots, src],
  )

  return {
    ImgComponent,
    src,
    alt,
    showFallback,
    getAvatarProps,
    getImageProps,
  }
}
